// Staff-note textarea with "@" mention picker (or type "@Full Name" by hand).
import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../lib/api";

let staffCache = null;
export function useStaff() {
  const [staff, setStaff] = useState(staffCache || []);
  useEffect(() => {
    if (staffCache) return;
    api.get("/users").then(({ data }) => {
      const seen = new Set();
      staffCache = (data || []).filter((u) => {
        const k = (u.name || "").trim().toLowerCase();
        if (!k || seen.has(k) || u.disabled || u.active === false || /^(test|access test)\b/i.test(u.name)) return false;
        seen.add(k); return true;
      });
      setStaff(staffCache);
    }).catch(() => {});
  }, []);
  return staff;
}

const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
// Longest name first + word boundary, so "@Raj Gami" never also counts as "@Raj".
export const mentionsIn = (text, staff) => {
  let low = text.toLowerCase(); const out = [];
  [...staff].sort((a, b) => b.name.length - a.name.length).forEach((u) => {
    const rx = new RegExp(`@${esc(u.name.toLowerCase())}(?![\\w])`, "g");
    if (rx.test(low)) { out.push(u.name); low = low.replace(rx, " "); }
  });
  return out;
};

// Render note text with known @mentions highlighted.
export function MentionText({ body, mentions = [] }) {
  if (!mentions.length) return body;
  const rx = new RegExp(`(@(?:${[...mentions].sort((a, b) => b.length - a.length).map(esc).join("|")}))(?![\\w])`, "gi");
  return String(body).split(rx).map((part, i) => (i % 2
    ? <span key={i} style={{ color: "#c4b5fd", background: "rgba(139,92,246,.18)", borderRadius: 4, padding: "0 3px", fontWeight: 700 }}>{part}</span>
    : part));
}

export default function MentionTextarea({ value, onChange, onSubmit, placeholder, testid }) {
  const staff = useStaff();
  const ref = useRef(null);
  const [q, setQ] = useState(null);
  const [idx, setIdx] = useState(0);
  const matches = useMemo(() => (q == null ? [] : staff.filter((u) => u.name.toLowerCase().split(/\s+/).some((w) => w.startsWith(q.toLowerCase())) || u.name.toLowerCase().startsWith(q.toLowerCase())).slice(0, 6)), [q, staff]);

  const detect = (text, caret) => {
    const m = text.slice(0, caret).match(/(?:^|\s)@([A-Za-z][\w'-]*(?: [A-Za-z][\w'-]*)?)?$/);
    setQ(m ? (m[1] || "") : null); setIdx(0);
  };
  const pick = (u) => {
    const el = ref.current; const caret = el?.selectionStart ?? value.length;
    const before = value.slice(0, caret).replace(/@[^@]*$/, `@${u.name} `);
    const next = before + value.slice(caret);
    onChange(next); setQ(null);
    setTimeout(() => { el?.focus(); el?.setSelectionRange(before.length, before.length); }, 0);
  };
  const onKeyDown = (e) => {
    if (q != null && matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => (i + 1) % matches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => (i - 1 + matches.length) % matches.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(matches[idx]); return; }
      if (e.key === "Escape") { setQ(null); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSubmit(); }
  };

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 0, display: "flex" }}>
      <textarea ref={ref} className="t2-input" rows={value.length > 90 ? 3 : 1} value={value} placeholder={placeholder} data-testid={testid}
        style={{ resize: "vertical", width: "100%" }}
        onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); }}
        onKeyDown={onKeyDown} onBlur={() => setTimeout(() => setQ(null), 150)} />
      {q != null && matches.length > 0 && (
        <div data-testid="mention-picker" style={{ position: "absolute", bottom: "calc(100% + 4px)", left: 0, zIndex: 30, minWidth: 220, maxWidth: "100%", background: "var(--t2-ink, #0F1420)", border: "1px solid rgba(139,92,246,.5)", borderRadius: 8, boxShadow: "0 10px 30px rgba(0,0,0,.5)", overflow: "hidden" }}>
          <div className="t2-mono" style={{ fontSize: 9.5, letterSpacing: ".1em", color: "#a78bfa", padding: "6px 10px 2px" }}>MENTION A COLLEAGUE</div>
          {matches.map((u, i) => (
            <button key={u.id || u.name} type="button" onMouseDown={(e) => { e.preventDefault(); pick(u); }} data-testid={`mention-option-${u.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", fontSize: 12.5, border: "none", cursor: "pointer", color: "var(--t2-white, #fff)", background: i === idx ? "rgba(139,92,246,.22)" : "transparent" }}>
              @{u.name}{u.email && <span style={{ color: "var(--t2-grey3)", fontSize: 10.5, marginLeft: 6 }}>{u.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
